import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import Layout from '../../components/layout/Layout';
import { StatusBadge, EstadoBadge } from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import Modal from '../../components/ui/Modal';
import { formatCurrency } from '../../utils/formatters';

const Produtos = () => {
  const [produtos, setProdutos]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [busca, setBusca]             = useState('');
  const [deletando, setDeletando]     = useState(null); // produto a deletar
  const [loadingDelete, setLoadingDelete] = useState(false);

  const fetchProdutos = async (search = '') => {
    setLoading(true);
    try {
      const params = search ? { busca: search } : {};
      const res = await api.get('/produtos', { params });
      setProdutos(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProdutos();
  }, []);

  // Debounce na busca
  useEffect(() => {
    const timer = setTimeout(() => fetchProdutos(busca), 400);
    return () => clearTimeout(timer);
  }, [busca]);

  const confirmarDelete = async () => {
    if (!deletando) return;
    setLoadingDelete(true);
    try {
      await api.delete(`/produtos/${deletando.id}`);
      setProdutos((prev) => prev.filter((p) => p.id !== deletando.id));
      setDeletando(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Produtos</h1>
          <p className="text-sm text-gray-500 mt-0.5">{produtos.length} produto(s)</p>
        </div>
        <Link to="/admin/produtos/novo" className="btn-primary">
          <PlusIcon className="w-4 h-4" />
          Novo produto
        </Link>
      </div>

      {/* Busca */}
      <input
        type="text"
        placeholder="Buscar por nome ou código..."
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        className="input mb-4"
      />

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : produtos.length === 0 ? (
        <EmptyState
          title="Nenhum produto cadastrado"
          description="Clique em 'Novo produto' para começar."
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Categoria</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Tamanho</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Preço</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {produtos.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{p.nome}</p>
                      {p.codigo && <p className="text-xs text-gray-400">{p.codigo}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{p.categoria_nome}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{p.tamanho || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={p.status} />
                        <EstadoBadge estado={p.estado} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                      {p.preco_mostruario ? formatCurrency(p.preco_mostruario) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <Link
                          to={`/admin/produtos/${p.id}`}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                          aria-label="Editar"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeletando(p)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Remover"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      <Modal
        open={!!deletando}
        onClose={() => setDeletando(null)}
        title="Remover produto"
      >
        <p className="text-sm text-gray-600 mb-6">
          Tem certeza que deseja remover <strong>{deletando?.nome}</strong>? O produto será
          desativado e não aparecerá mais no catálogo.
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setDeletando(null)} className="btn-secondary">Cancelar</button>
          <button onClick={confirmarDelete} disabled={loadingDelete} className="btn-danger">
            {loadingDelete ? 'Removendo...' : 'Remover'}
          </button>
        </div>
      </Modal>
    </Layout>
  );
};

export default Produtos;
